// import jszip
import initSqlJs from "sql.js";
import JSZip from "jszip";
import { OnFileEventFn, OnFileFn } from "../../FileProcessor";
import { isNotEmpty } from "../../core/isEmpty";
import { qnote } from "../Helpers/Note";
import { lookup } from "mime-types";
import { processFieldIfs, processFields } from "../../linked/Helpers";
import { Pack } from "../../Types";

const IMG_REGEX = /\<img.+?\n*src=['"](.+?)['"]/gm;
const SOUND_REGEX = /\[sound:(.+?)\]/gm;

let SQL: any = null;

async function init() {
  SQL = await initSqlJs({
    locateFile: (file) => `/static/js/sql-wasm.wasm`,
  });
}
init();

// const zip = new JSZip();

type ApkgModel = {
  id: string;
  name: string;

  css?: string;
  did?: string;

  flds: {
    name: string;
    ord: number;
  }[];
  tmpls: {
    name: string;
    ord: number;
    qfmt: string;
    afmt: string;
  }[];
};

type ApkgDeck = {
  id: string;
  name: string;
  desc: string;
  mid?: string;
};

// Flattens decks
type IntermediateApkgFormat = {
  templates: {
    [id: string]: string;
  };
  cards: {
    id: string;
    templateId: string;
    fields: {
      [id: string]: string;
    };
  }[];
  fields: {
    [id: string]: string;
  };
};

const getMediaUsedByCardTemplates = (
  // IntermediateApkgFormat.cards
  pkg: IntermediateApkgFormat,
  mediaMap: { [id: string]: string },
): Set<string> => {
  const usedMedia = new Set<string>();
  for (const card of pkg.cards) {
    for (const side of ["front", "back"]) {
      const template = pkg.templates[`${card.templateId}_${side}`];
      let parsed = processFieldIfs(template, card.fields);
      if (!parsed) {
        continue;
      }
      parsed = processFields(parsed, {}, card.fields);
      // console.log(parsed);

      IMG_REGEX.lastIndex = 0;
      SOUND_REGEX.lastIndex = 0;
      let imageMatches: RegExpExecArray | null = null;
      let soundMatches: RegExpExecArray | null = null;
      do {
        imageMatches = IMG_REGEX.exec(parsed);
        soundMatches = SOUND_REGEX.exec(parsed);
        if (imageMatches) {
          const mediaId = imageMatches[1];
          if (mediaMap[mediaId]) {
            usedMedia.add(mediaMap[mediaId]);
          }
        }
        if (soundMatches) {
          const mediaId = soundMatches[1];
          if (mediaMap[mediaId]) {
            usedMedia.add(mediaMap[mediaId]);
          }
        }
      } while (imageMatches || soundMatches);
    }
  }
  console.log(
    "of all media",
    Object.values(mediaMap).length,
    "used",
    usedMedia.size,
  );
  return usedMedia;
};

const attachAudioToCards = (
  pkg: IntermediateApkgFormat,
): IntermediateApkgFormat => {
  for (const card of pkg.cards) {
    for (const side of ["front", "back"]) {
      const template = pkg.templates[`${card.templateId}_${side}`];
      let parsed = processFieldIfs(template, card.fields);
      if (!parsed) {
        continue;
      }
      parsed = processFields(parsed, {}, card.fields);

      SOUND_REGEX.lastIndex = 0;
      let soundMatches: RegExpExecArray | null = null;
      do {
        soundMatches = SOUND_REGEX.exec(parsed);
        console.log("soundMatches", soundMatches);
        if (soundMatches) {
          const mediaId = soundMatches[1];
          const mediaName = mediaId.replace(/[^a-zA-Z0-9-]/g, "_");

          if (side === "front") {
            card.fields["__audio_front"] = mediaName;
          } else {
            card.fields["__audio_back"] = mediaName;
          }

          // console.log("card.fields", card.fields)
        }
      } while (soundMatches);
    }

    // empty out field that has this audio set:
    for (const fieldId in card.fields) {
      if (card.fields[fieldId].match(SOUND_REGEX) || card.fields[fieldId] === "") {
        delete card.fields[fieldId];
      }
    }
  }
  return pkg;
};

export const conformId = (id: string) => {
  return id.replace(/[^a-zA-Z0-9-]/g, "_");
};

export async function processApkg(
  file: File,
  onFile: OnFileFn,
  onFileEvent: OnFileEventFn,
  onChooseDeck: (decks: string[]) => Promise<string>,
): Promise<any> {
  onFileEvent({ type: "import_start", filetype: "apkg" });
  const zip = new JSZip();
  const zipFile = await zip.loadAsync(file);

  //----------------------
  // Format: .apkg
  // Files:
  // 0->9999              images, sounds
  // collection.anki2     sqlite database
  // media                json mapping from file number to image name
  // ----------------------

  // Iterate through files and add media
  let mediaFileNames: any[] = [];
  let mediaFileExists = false;
  let collectionFileName: string | null = null;

  for (const fileName in zipFile.files) {
    const zipEntry = zipFile.files[fileName];
    // If matches /[0-9]+/
    if (zipEntry.name.match(/^[0-9]+$/)) {
      // console.log("found", zipEntry.name, zipEntry.name.match(/[0-9]+/));
      mediaFileNames.push(zipEntry.name);
    }
    // If matches collection.anki2
    if (zipEntry.name === "collection.anki2" && collectionFileName === null) {
      collectionFileName = zipEntry.name;
    } else if (zipEntry.name === "collection.anki21") {
      collectionFileName = zipEntry.name;
    }

    // If matches media
    if (zipEntry.name = "media") {
      mediaFileExists = true;
    }
  }

  if (!collectionFileName) {
    console.error("No collection file found");
    return;
  }

  if (!mediaFileExists) {
    console.error("No media file found");
    return;
  }

  console.log("apkg is formatted successfully!");
  console.log("found", mediaFileNames.length, "media files");

  // open media file as json
  const mediaJson = await zipFile.file("media")?.async("string")!;
  const media = JSON.parse(mediaJson);

  // map images to media
  const mediaMapAsArray = mediaFileNames.map((filename) => {
    return [media[filename], filename];
  });

  // flatten 2-tuple array [a, b] to (a: b) map
  const mediaMap = mediaMapAsArray.reduce((acc, cur) => {
    const [key, value] = cur;
    acc[key] = value;
    return acc;
  }, {});

  const doneArray: string[] = [];

  // console.log(SQL)
  const collectionFile = await zipFile.file(collectionFileName!)?.async(
    "uint8array",
  )!;
  const db = new SQL!.Database(collectionFile);

  // Wait on setTimeout
  await new Promise((resolve) => setTimeout(resolve));
  onFileEvent({
    type: "import_progress",
    // progress: i / mediaFileNames.length,
    progress: 0.9,
  });

  // Extract fields (according to README-apkg.md)

  // Steps:
  // 1. extract decks                col[_].decks[i].
  // 1b extract fields & tmpl        col[_].models[i].[flds/tmpl][j]
  // ---> WARN: ignoring everything in flds for now besides name]
  // ---> join models on 'did'

  // 2. extract notes         notes[_].fld[j].[name,qmft,afmt,id]
  // 3. extract cards         cards[_].[nid,did,ord]
  // 4. transform them into
  //    optimem's format

  // 1. extract field names
  const rawResponse = db.exec(`SELECT models,decks FROM col`);
  const models = Object.values(
    JSON.parse(rawResponse[0].values[0][0]),
  ) as ApkgModel[];
  const decks = Object.values(
    JSON.parse(rawResponse[0].values[0][1]),
  ) as ApkgDeck[];

  console.log("raw", models, decks);

  let primaryDeck = decks[1];

  // TODO: Handle multiple decks/models
  if (decks.length > 2) {
    console.error("Multiple decks found. Only using first one.", decks);

    const selectedDeck = await onChooseDeck(decks.map((x) => x.name));
    primaryDeck = decks.find((x) => x.name === selectedDeck)!;
  }

  const intermediateModels = models.map((model) => ({
    id: model.id,
    fields: model.flds.map((field) => ({ name: field.name })),
    templates: model.tmpls.map((template) => ({
      ...template,
      id: template.ord,
    })),
    css: model.css,
  }));
  const model = intermediateModels[intermediateModels.length - 1];

  console.log("MODELS ---");
  console.log(intermediateModels);

  // 2. extract notes' fields
  const notesRes = db.exec(
    "SELECT id,flds FROM notes",
  );

  const notes = notesRes[0].values.map((note) => {
    const [id, flds] = note;
    return { id, fields: flds.split("\u001f") };
  });

  console.log("NOTES ---");
  console.log(notes);

  // 3. extract cards
  const cardsRes = db.exec(
    "SELECT id, nid, did, ord FROM cards",
  );

  let cards = cardsRes[0].values.map((card) => {
    const [id, nid, did, ord] = card;
    return { id, nid, did, templateId: ord };
  });

  console.log("beforecards", cards.length);
  cards = cards.filter((card) => card.did === primaryDeck.id);
  console.log("aftercards", cards.length);

  console.log("CARDS ---");
  console.log(cards);

  // 4. transform them into optimem's format
  const intermediateApkgFormat: IntermediateApkgFormat = {
    templates: {},
    cards: [],
    fields: {},
  };

  // 4.0 fields
  for (const field of model.fields) {
    intermediateApkgFormat.fields[field.name] = "";
  }

  // 4.1. templates
  for (const template of model.templates) {
    const qNotEmpty = isNotEmpty(template.qfmt);
    const aNotEmpty = isNotEmpty(template.afmt);
    // console.log("template", template.id, template.qfmt, template.qfmt.length, template.afmt.length,  qNotEmpty, aNotEmpty, qNotEmpty && aNotEmpty, qNotEmpty || aNotEmpty);

    if (qNotEmpty && aNotEmpty) {
      const qfmtId = `${template.id}_front`;
      intermediateApkgFormat.templates[qfmtId] = template.qfmt;

      const afmtId = `${template.id}_back`;
      intermediateApkgFormat.templates[afmtId] = template.afmt;

      console.log("qfmt", qfmtId, template.qfmt);
      console.log("afmt", afmtId, template.afmt);
    } else if (qNotEmpty || aNotEmpty) {
      const fmtId = `${template.id}`;
      intermediateApkgFormat.templates[fmtId] = template.qfmt ||
        template.afmt;
    }
  }

  if (model.css) {
    intermediateApkgFormat.templates["css"] = model.css;
  }

  // 4.2. cards
  // 4.2.1 map notes fields to cards
  for (const card of cards) {
    const { id: cardId, nid, did, templateId } = card;
    const { fields: fieldsUnmapped } = notes.find((note) => note.id === nid)!;

    const fieldsMapped = fieldsUnmapped.reduce((acc, cur, i) => {
      const field = model.fields[i];
      if (field) {
        acc[field.name] = cur;
      }
      return acc;
    }, {});

    intermediateApkgFormat.cards.push({
      id: `${cardId}`,
      templateId,
      fields: fieldsMapped,
    });
  }

  attachAudioToCards(intermediateApkgFormat);

  // 5. pack info
  const name = (primaryDeck as any).name;
  const description = (primaryDeck as any).desc;

  // Wait on setTimeout
  await new Promise((resolve) => setTimeout(resolve));
  onFileEvent({
    type: "import_progress",
    // progress: i / mediaFileNames.length,
    progress: 0.95,
  });

  console.log("INTERMEDIATE APKG FORMAT ---");
  console.log(intermediateApkgFormat);

  const usedMedia = getMediaUsedByCardTemplates(
    intermediateApkgFormat,
    mediaMap,
  );

  /// LOAD media
  let i = 0;
  const mediaMapKeys = Object.keys(mediaMap);
  const mediaMapValues = Object.values(mediaMap);
  await Promise.all(
    [...usedMedia].map(async (shortName: string) => {
      const fullNameIndex = mediaMapValues.indexOf(shortName);
      const fullName = mediaMapKeys[fullNameIndex] as string;
      const file = await zipFile.file(shortName as string)?.async("blob")!;
      const fileType = lookup(fullName);

      // console.log("loaded", fullName, file, fileType);

      onFile(file, fullName, fileType);
      doneArray.push(fullName);
      onFileEvent({
        type: "import_progress",
        // progress: i / mediaFileNames.length,
        progress: (doneArray.length / mediaFileNames.length) * 0.8,
      });
    }),
  );

  // Wait on setTimeout
  await new Promise((resolve) => setTimeout(resolve));
  onFileEvent({
    type: "import_progress",
    // progress: i / mediaFileNames.length,
    progress: 0.85,
  });

  console.log("MEDIA ------", usedMedia);
  // Media is loaded.

  onFile(
    {
      metadata: {
        name,
        description,
        tags: ["#anki", "#apkg", "#imported"],
        id: conformId(`${file.name.split(".apkg")[0]}`),
        url: "",
        version: "0.0.1",
        created: Number(new Date()),
        updated: Number(new Date()),
        contentType: "application/json",
        size: 0,
      },
      data: {
        reps: {
          Front: {
            promptText: "Question",
          },
          Back: {
            promptText: "Answer",
          },
          ...Object.keys(intermediateApkgFormat.fields).reduce(
            (acc, cur, i) => {
              acc[cur] = {};
              return acc;
            },
            {},
          ),
        },
        // fields: intermediateApkgFormat.fields,
        templates: intermediateApkgFormat.templates,
        concepts: intermediateApkgFormat.cards.map((card) => ({
          id: card.id,
          reps: {
            Front: {
              text: `{{ template:${card.templateId}_front }}`,
              audio: card.fields.__audio_front != null
                ? card.fields.__audio_front
                : null,
            },
            Back: {
              text: `{{ template:${card.templateId}_back }}`,
              audio: card.fields.__audio_back != null
                ? card.fields.__audio_back
                : null,
            },
            // Map fields to reps with text
            ...Object.keys(card.fields).reduce((acc, cur, i) => {
              if (cur === "__audio_front" || cur === "__audio_back") {
                return acc;
              }
              const value = card.fields[cur];
              acc[cur] = {
                text: value,
              };
              return acc;
            }, {}),
          },
        })),
        wiring: ["Front>Back"],
      },
      extra: {},
    } as Pack,
    "pack.apkg",
    "application/json",
  );
  onFileEvent({ type: "import_end", filetype: "apkg" });
}
